import React, { useRef, useState, useEffect, useCallback, useMemo, Suspense } from "react";
import * as THREE from "three";
import { useThree } from '@react-three/fiber';
import DraggableObject from "./DraggableObject";
import { SvgType, zIndexEpsilon } from "../../types/floorplannerTypes";
import { BoundingBox, SelectableSymbol } from './SelectableSymbol';
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import { Svg } from "./Svg";
import { editorStore } from "../../store/editorStore";
import { observer } from "mobx-react-lite";
import { getEffectiveSvgFlipState } from "../../utils/symbolFlipState";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../utils/opacityRemount";
import { isSymbolAspectRatioLocked } from "../../utils/symbolAspectRatio";
interface SvgSymbolProps {
  svg: SvgType;
  onDragStart: (svg: SvgType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
  renderOrder?: number;
}

const SvgSymbol: React.FC<SvgSymbolProps> = observer(({
  svg,
  onDragStart,
  onDragEnd,
  onDrag,
  renderOrder = 0
}) => {
  const { gl, camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);

  const [svgLength, setSvgLength] = useState(svg.svgLength);
  const [svgHeight, setSvgHeight] = useState(svg.svgHeight);
  const svgLengthRef = useRef(svg.svgLength);
  const svgHeightRef = useRef(svg.svgHeight);
  const [localPosition, setLocalPosition] = useState<[number, number]>([svg.position.x, svg.position.y]);
  const aspectRatio = svg.svgRatio || 1;
  const aspectLocked = isSymbolAspectRatioLocked(svg.lockAspectRatio);

  const {
    flipped: effectiveFlipped,
    flipHorizontal: effectiveFlipHorizontal,
    flipVertical: effectiveFlipVertical,
  } = getEffectiveSvgFlipState(svg);

  const lineColor = svg.lineColor ?? floorplannerStore.lineColor;
  const [pivot, setPivot] = useState<[number, number]>([0, 0]);
  const dragOffset = useRef({ x: 0, y: 0 });

  const lineWeight = svg.lineWeight ?? floorplannerStore.symbolLineWeight;
  const planLineType = svg.lineType === "dashed" ? "dashed" : "solid";
  const usesUserModelFootprint = Boolean(svg.threeD?.userModel);
  const isDefaultSvgFillColor =
    svg.fillColor === undefined ||
    svg.fillColor === null ||
    svg.fillColor === floorplannerStore.fillColor ||
    svg.fillColor === 0xffffff;
  const svgFillOverrideColor = svg.fillPattern || !isDefaultSvgFillColor
    ? (svg.fillColor ?? "")
    : "";

  const [centerPosition] = useState<[number, number]>([0, 0]);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const epsilon = 0.0;

  const calculateBoundingBox = useCallback((): BoundingBox => {
    // Use stored size instead of cloning tessellated SVG meshes — setFromObject
    // on 100+ product footprints made pointermove hitch.
    const width = Math.max(svgLength || 0, 0.01)
    const height = Math.max(svgHeight || 0, 0.01)
    return {
      topLeft: [-width / 2, height / 2],
      topRight: [width / 2, height / 2],
      bottomLeft: [-width / 2, -height / 2],
      bottomRight: [width / 2, -height / 2],
      width,
      height,
      depth: 0,
    };
  }, [svgLength, svgHeight]);

  useEffect(() => {
    if (isResizing) return;
    setSvgLength(svg.svgLength);
    setSvgHeight(svg.svgHeight);
    svgLengthRef.current = svg.svgLength;
    svgHeightRef.current = svg.svgHeight;
    setLocalPosition([svg.position.x, svg.position.y]);
  }, [isResizing, svg.svgLength, svg.svgHeight, svg.position.x, svg.position.y]);

  useEffect(() => {
    editorStore.updateGroupRef(svg.id, groupRef.current);
  }, [groupRef.current]);

  // Redraw when the symbol.selected changes
  useEffect(() => {
  }, [svg.selected]);

  return (
    <group
      position={[0, 0, svg.zIndex * zIndexEpsilon]}
    >
      <DraggableObject
        position={localPosition}
        onDragStart={(offset) => {
          setIsDragging(true);
          onDragStart(svg, offset)
        }}
        onDragEnd={(endPosition) => {
          onDragEnd(endPosition)
          setIsDragging(false);
        }}
        onDrag={onDrag}
        selectable={true}
        attachmentId={svg.id}
        symbol={svg}
      >
        <SelectableSymbol
          ref={groupRef}
          handleSize={floorplannerStore.symbolHandleSize}
          calculateBoundingBox={calculateBoundingBox}
          onResizeStart={() => setIsResizing(true)}
          onResize={(newWidth, newHeight, _boundingBox, _handle, newPosition) => {
            const minContentSize = 0.01;
            const safeAspectRatio = aspectRatio || 1;
            const contentWidth = Math.max(newWidth, minContentSize);
            const contentHeight = aspectLocked
              ? Math.max(contentWidth / safeAspectRatio, minContentSize)
              : Math.max(newHeight, minContentSize);

            setSvgLength(contentWidth);
            setSvgHeight(contentHeight);
            svgLengthRef.current = contentWidth;
            svgHeightRef.current = contentHeight;
            if (newPosition) {
              setLocalPosition(newPosition);
            }
          }}
          onResizeEnd={(_event, finalResize) => {
            setIsResizing(false);
            floorplannerStore.updateSymbolProperty(svg.id, "svgLength", svgLengthRef.current);
            floorplannerStore.updateSymbolProperty(svg.id, "svgHeight", svgHeightRef.current);
            if (finalResize?.position) {
              floorplannerStore.updateSymbolProperty(svg.id, "position", finalResize.position);
            }
          }}
          center={centerPosition}
          rotation={svg.rotation}
          symbol={svg}
          drawHandles={true}
          showSelectionOutline={!usesUserModelFootprint}
          lockAspectRatio={aspectLocked}
          isDragging={isDragging}
        >
          <group
            ref={groupRef}
            rotation={[0, 0, svg.rotation || 0]}
            onPointerEnter={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
            }}
            onPointerLeave={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
            }}
          >
            <Suspense
              fallback={
                <mesh visible={false}>
                  <planeGeometry
                    args={[
                      Math.max(svgLength, 0.01),
                      Math.max(svgHeight, 0.01),
                    ]}
                  />
                </mesh>
              }
            >
              <Svg
                // Remount when opacity / line type changes so fills + strokes update
                // together (drei Line often ignores live prop updates).
                key={`opacity-${Math.round(resolveFillOpacity(svg) * 100)}-${planLineType}-${getOpacityRemountEpoch(svg.id)}`}
                svgId={svg.id}
                src={svg.svgPath}
                fillMaterial={{
                  color: svgFillOverrideColor,
                }}
                fillOpacity={resolveFillOpacity(svg)}
                fillPattern={svg.fillPattern}
                lineColor={lineColor ?? "black"}
                lineWeight={lineWeight}
                width={svgLength}
                height={svgHeight}
                lineType={planLineType}
                lockAspectRatio={aspectLocked}
                renderOrder={renderOrder}
                flipped={effectiveFlipped}
                flipHorizontal={effectiveFlipHorizontal}
                flipVertical={effectiveFlipVertical}
                libraryKey={svg.libraryKey}
                hasProduct3D={Boolean(svg.threeD?.product)}
                hasUserModel3D={Boolean(svg.threeD?.userModel)}
              />
            </Suspense>
          </group>
        </SelectableSymbol>
      </DraggableObject>
    </group>
  );
});

export default SvgSymbol;
